import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, TrendingUp, Newspaper, BarChart3, PenLine } from "lucide-react";
import { api } from "../lib/api";
import Seo from "../components/Seo";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "";

function InsightCard({ item }) {
  const inner = (
    <>
      <div className="flex flex-wrap items-center gap-2 mb-1.5">
        <span className="text-[10px] font-bold uppercase tracking-wider bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded">{item.category}</span>
        {item.featured && <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Featured</span>}
      </div>
      <p className="font-semibold text-zinc-900 text-sm leading-snug group-hover:text-blue-800 transition-colors duration-150">{item.title}</p>
      {item.summary && <p className="text-xs text-zinc-500 mt-1.5 leading-relaxed line-clamp-2">{item.summary}</p>}
      <p className="text-[11px] text-zinc-400 mt-2">
        {item.origin === "original" ? "HiredToday" : item.source_name}{item.published_at ? ` · ${fmtDate(item.published_at)}` : ""}
      </p>
    </>
  );
  if (item.origin === "original") {
    return (
      <Link to={`/insights/${item.slug}`} data-testid={`insight-item-${item.id}`}
        className="group block border border-zinc-200 rounded-md p-4 bg-white hover:border-zinc-400 transition-colors duration-150">
        {inner}
      </Link>
    );
  }
  return (
    <a href={item.link} target="_blank" rel="noopener noreferrer" data-testid={`insight-item-${item.id}`}
      className="group block border border-zinc-200 rounded-md p-4 bg-white hover:border-zinc-400 transition-colors duration-150">
      {inner}
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 mt-2">
        Read at source <ArrowUpRight className="h-3 w-3" />
      </span>
    </a>
  );
}

export default function Resources() {
  const { hash } = useLocation();
  const [insights, setInsights] = useState(null);
  const [trends, setTrends] = useState(null);

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [hash]);

  useEffect(() => {
    api.get("/insights").then((r) => setInsights(r.data)).catch(() => setInsights({ items: [] }));
    api.get("/insights/trends").then((r) => setTrends(r.data)).catch(() => {});
  }, []);

  const items = insights?.items || [];
  const news = items.filter((i) => i.origin === "external" && i.category !== "Labour market data").slice(0, 9);
  const labourData = items.filter((i) => i.category === "Labour market data").slice(0, 6);
  const advice = items.filter((i) => i.origin === "original").slice(0, 6);

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20" data-testid="resources-page">
      <Seo
        title="Career & Market Insights | HiredToday"
        description="UK career news, official labour-market data and real hiring trends from HiredToday, plus practical guidance for jobseekers and employers."
        path="/resources"
      />
      <div className="max-w-2xl">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">Career &amp; market insights</p>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-black text-zinc-950">
          What's happening in UK work, plainly told.
        </h1>
        <p className="mt-5 text-base sm:text-lg text-zinc-600 leading-relaxed">
          News and official statistics from authorised sources, real trends from the HiredToday board,
          and our own straight-talking guidance. We link out to the original publishers, we never lift their work.
        </p>
      </div>

      {trends && (
        <section className="mt-12" data-testid="trends-section">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-4 w-4 text-blue-600" />
            <h2 className="font-display text-2xl font-bold text-zinc-950">Trending on HiredToday right now</h2>
          </div>
          <p className="text-xs text-zinc-400 mb-4">Calculated from genuine live listings and applications on this board, updated {fmtDate(trends.as_of)}.</p>
          <div className="grid sm:grid-cols-3 gap-4 items-start">
            <div className="border border-zinc-200 rounded-md p-5 bg-white" data-testid="trend-totals">
              <p className="font-display text-3xl font-black text-zinc-950">{trends.live_total_jobs.toLocaleString("en-GB")}</p>
              <p className="text-xs font-semibold text-zinc-500 mt-1">live jobs right now, {trends.live_native_jobs.toLocaleString("en-GB")} posted directly on HiredToday</p>
              <p className="font-display text-3xl font-black text-zinc-950 mt-4">{trends.applications_this_week.toLocaleString("en-GB")}</p>
              <p className="text-xs font-semibold text-zinc-500 mt-1">applications sent this week</p>
            </div>
            <div className="border border-zinc-200 rounded-md p-5 bg-white" data-testid="trend-categories">
              <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Most active industries</p>
              <ul className="space-y-2">
                {trends.trending_categories.map((c) => (
                  <li key={c.category} className="flex items-center justify-between text-sm">
                    <span className="text-zinc-700">{c.category}</span>
                    <span className="text-xs font-semibold text-zinc-500">
                      {c.live_jobs} live
                      {c.new_this_week > c.new_last_week && <span className="ml-1.5 text-green-600">▲</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="border border-zinc-200 rounded-md p-5 bg-white" data-testid="trend-locations">
              <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Busiest locations</p>
              <ul className="space-y-2">
                {trends.trending_locations.slice(0, 6).map((l) => (
                  <li key={l.location} className="flex items-center justify-between text-sm">
                    <Link to={`/jobs?location=${encodeURIComponent(l.location)}`} className="text-blue-700 hover:text-blue-900">{l.location}</Link>
                    <span className="text-xs font-semibold text-zinc-500">{l.live_jobs} live</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {trends.salary_snapshot.length > 0 && (
            <div className="mt-4 border border-zinc-200 rounded-md p-5 bg-white" data-testid="salary-snapshot">
              <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Typical advertised salaries right now</p>
              <div className="flex flex-wrap gap-x-8 gap-y-2">
                {trends.salary_snapshot.map((s) => (
                  <p key={s.category} className="text-sm text-zinc-700">
                    <span className="font-semibold text-zinc-950">£{s.median.toLocaleString("en-GB")}</span> median · {s.category}
                    <span className="text-xs text-zinc-400"> ({s.jobs_with_salary} listings)</span>
                  </p>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      <section className="mt-14" data-testid="news-section">
        <div className="flex items-center gap-2 mb-4">
          <Newspaper className="h-4 w-4 text-blue-600" />
          <h2 className="font-display text-2xl font-bold text-zinc-950">Career &amp; industry news</h2>
        </div>
        <p className="text-xs text-zinc-400 mb-4">From GOV.UK, DWP, Skills England and other authorised sources. Summaries are the publishers' own; the full story lives on their site.</p>
        {!insights ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[0, 1, 2].map((i) => <div key={`resource-skel-${i}`} className="h-36 rounded-md bg-zinc-100 animate-pulse" />)}</div>
        ) : news.length === 0 ? (
          <p className="text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-md p-8 text-center">Fresh items land here after the next daily check.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {news.map((item) => <InsightCard key={item.id} item={item} />)}
          </div>
        )}
      </section>

      {labourData.length > 0 && (
        <section className="mt-14" data-testid="labour-data-section">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="h-4 w-4 text-blue-600" />
            <h2 className="font-display text-2xl font-bold text-zinc-950">Official labour-market data</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {labourData.map((item) => <InsightCard key={item.id} item={item} />)}
          </div>
        </section>
      )}

      {advice.length > 0 && (
        <section className="mt-14" data-testid="advice-section">
          <div className="flex items-center gap-2 mb-4">
            <PenLine className="h-4 w-4 text-blue-600" />
            <h2 className="font-display text-2xl font-bold text-zinc-950">Guides from the HiredToday team</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {advice.map((item) => <InsightCard key={item.id} item={item} />)}
          </div>
        </section>
      )}

      <section className="mt-16 max-w-3xl" data-testid="resources-candidates">
        <h2 className="font-display text-2xl font-bold text-zinc-950">For jobseekers</h2>
        <div className="mt-5 space-y-6 text-sm sm:text-base text-zinc-600 leading-relaxed">
          <div>
            <h3 className="font-semibold text-zinc-900">Salary information</h3>
            <p className="mt-1.5">
              Every HiredToday listing shows its salary whenever the employer provides one, so you can compare
              roles before you apply. Use the search filters on the <Link to="/jobs" className="text-blue-700 font-medium hover:text-blue-900">jobs page</Link> to
              narrow by title and location, and read the salary line on each card.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-zinc-900">Applying well</h3>
            <p className="mt-1.5">
              Keep your profile accurate and current, employers see what you share, and your CV is built from it.
              A short, honest summary beats a long generic one. When you get an interview invitation, picking a
              time quickly keeps you near the top of the employer's list.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-zinc-900">Job alerts</h3>
            <p className="mt-1.5">
              Save a search on the jobs page and HiredToday emails you when matching roles go live. You can turn
              alerts on or off any time from your dashboard.
            </p>
          </div>
        </div>
      </section>

      <section id="employers" className="mt-14 max-w-3xl scroll-mt-24" data-testid="resources-employers">
        <h2 className="font-display text-2xl font-bold text-zinc-950">For employers</h2>
        <div className="mt-5 space-y-6 text-sm sm:text-base text-zinc-600 leading-relaxed">
          <div>
            <h3 className="font-semibold text-zinc-900">Writing a listing that works</h3>
            <p className="mt-1.5">
              Clear titles, honest salary ranges and a short list of real requirements attract better applicants
              than long wish-lists. Our AI helper can draft a description from your notes, you always review it
              before anything goes live.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-zinc-900">International recruitment</h3>
            <p className="mt-1.5">
              You can hire candidates based outside the UK, but you are responsible for checking every worker's
              right to work before they start, whatever their nationality. HiredToday flags Right to Work in your
              checks pipeline so it is never skipped. If you are unsure, ask us via the
              <Link to="/contact" className="text-blue-700 font-medium hover:text-blue-900"> contact page</Link>.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-zinc-900">Recruiter support</h3>
            <p className="mt-1.5">
              Hiring regularly or in volume? <Link to="/plans" className="text-blue-700 font-medium hover:text-blue-900">Subscription plans</Link> bring
              the per-listing cost down, and our team is on
              <a href="mailto:office@hiredtoday.co.uk" className="text-blue-700 font-medium hover:text-blue-900"> office@hiredtoday.co.uk</a> for
              anything bespoke.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
